

import { useState } from "react"
import Button from "../ui/button"
import { Card } from "../ui/card"
import { ImageIcon, Code, LineChart, Network, Lightbulb, RefreshCw } from "lucide-react"

const ConceptVisualizer = ({ onSubmit }) => {
  const [concept, setConcept] = useState("")
  const [visualizationType, setVisualizationType] = useState("diagram")
  const [isLoading, setIsLoading] = useState(false)

  const visualizationTypes = {
    diagram: {
      icon: <Network className="h-4 w-4 mr-2" />,
      label: "Diagram",
      prompt: "Create a detailed diagram explaining the concept of",
    },
    code: {
      icon: <Code className="h-4 w-4 mr-2" />,
      label: "Code Example",
      prompt: "Provide a code example that demonstrates the concept of",
    }
  }

  const generateVisualization = async () => {
    if (!concept) return

    setIsLoading(true)

    try {
      // Make a direct API call to the concept visualizer endpoint
      const response = await fetch("/api/visualize-concept", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          concept,
          visualizationType,
        }),
      })

      const data = await response.json()

      if (data.success) {
        // Format the visualization for display in the chatbox
        const formattedContent = `# ${visualizationTypes[visualizationType].label} for "${concept}"\n\n${data.visualization}`

        // Pass the configuration and formatted content to the parent component
        if (onSubmit) {
          const config = {
            concept,
            visualizationType,
            toolType: "visualize",
            type: "visualize",
            response: formattedContent, // Add the response directly to the config
          }
          onSubmit(config)
        }
      } else {
        console.error("Error generating visualization:", data.error)
        // If the API call fails, fall back to using the chat API
        fallbackToChatAPI()
      }
    } catch (error) {
      console.error("Error calling concept visualizer API:", error)
      // If there's an error with the API call, fall back to using the chat API
      fallbackToChatAPI()
    } finally {
      setIsLoading(false)
    }
  }

  // Fallback method that uses the chat API through the parent component
  const fallbackToChatAPI = () => {
    const type = visualizationTypes[visualizationType]
    const formattedContent =
      `# ${type.label} for "${concept}"\n\n` +
      "I'll create a visualization to help explain this concept.\n\n" +
      "This will include:\n" +
      "- A clear title and structure\n" +
      "- Key components clearly labeled\n" +
      "- Brief explanations of how this helps understand the concept\n\n" +
      "Please wait while I generate the visualization..."

    // Pass the configuration and formatted content to the parent component
    if (onSubmit) {
      const config = {
        concept,
        visualizationType,
        toolType: "visualize",
        type: "visualize",
        response: formattedContent, // Add the response directly to the config
        fallback: true,
      }
      onSubmit(config)
    }
  }

  return (
    <Card className="p-4 bg-white shadow-lg border border-gray-200 rounded-xl mb-6">
      <div className="flex items-center mb-4">
        <Lightbulb className="mr-2 h-5 w-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-gray-800">Concept Visualizer</h3>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Enter a concept you want to visualize:</label>
        <input
          type="text"
          value={concept}
          onChange={(e) => setConcept(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          placeholder="e.g., Photosynthesis, Binary Search, Quadratic Equations..."
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Visualization Type:</label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {Object.entries(visualizationTypes).map(([key, type]) => (
            <button
              key={key}
              onClick={() => setVisualizationType(key)}
              className={`flex items-center justify-center p-2 rounded-lg ${
                visualizationType === key
                  ? "bg-amber-100 text-amber-800 border border-amber-300"
                  : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
              }`}
            >
              {type.icon}
              <span className="ml-2">{type.label}</span>
            </button>
          ))}
        </div>

        <div className="p-4 bg-gray-50 rounded-md">
          <div className="flex items-start">
            <div className="bg-amber-100 p-2 rounded-full mr-3">{visualizationTypes[visualizationType].icon}</div>
            <div>
              <h4 className="font-medium text-gray-800">{visualizationTypes[visualizationType].label}</h4>
              <p className="text-sm text-gray-600">
                {visualizationType === "diagram" && "Creates a visual diagram showing relationships and processes"}
                {visualizationType === "code" && "Provides a code example that demonstrates the concept"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Button
        onClick={generateVisualization}
        disabled={!concept || isLoading}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg"
      >
        {isLoading ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            {visualizationTypes[visualizationType].icon}
            Generate {visualizationTypes[visualizationType].label}
          </>
        )}
      </Button>
    </Card>
  )
}

export default ConceptVisualizer
